import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from './button';

interface ScrollToTopButtonProps {
  onScrollToTop: () => void;
  onScrollToBottom?: () => void;
  isAtTop: boolean;
  isAtBottom: boolean;
  className?: string;
}

export const ScrollToTopButton: React.FC<ScrollToTopButtonProps> = ({
  onScrollToTop,
  onScrollToBottom,
  isAtTop,
  isAtBottom,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);

  // Show button when scrolled down
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(!isAtTop);
    };

    // Check initial state
    handleScroll();

    // Listen for scroll changes
    const interval = setInterval(handleScroll, 100);
    return () => clearInterval(interval);
  }, [isAtTop]);

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        className={`fixed bottom-4 right-4 z-50 ${className}`}
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: 20 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex flex-col gap-2">
          {/* Scroll to Top Button */}
          <Button
            onClick={onScrollToTop}
            size="sm"
            variant="outline"
            className="w-10 h-10 p-0 rounded-full bg-white/90 backdrop-blur-md border-white/50 shadow-lg hover:bg-white hover:shadow-xl transition-all duration-200"
            title="Scroll to top"
          >
            <ChevronUp size={16} className="text-gray-600" />
          </Button>

          {/* Scroll to Bottom Button (if provided and not at bottom) */}
          {onScrollToBottom && !isAtBottom && (
            <Button
              onClick={onScrollToBottom}
              size="sm"
              variant="outline"
              className="w-10 h-10 p-0 rounded-full bg-white/90 backdrop-blur-md border-white/50 shadow-lg hover:bg-white hover:shadow-xl transition-all duration-200"
              title="Scroll to bottom"
            >
              <ChevronDown size={16} className="text-gray-600" />
            </Button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
